'use client'

/**
 * Progress-dots bar — Figma "progress-steps-bar" node 2397:71094, built from
 * the two-state atoms `dot` (2397:71026) and `line` (2397:71038).
 * The Figma component exposes a step property 0–10; the appraisal and
 * offer-to-purchase wizards both render 5.
 *
 * Dot: 24px slot with a 12px inner ellipse
 *   - reached step (step <= currentStep) → theme/foreground #000000 (bg-foreground)
 *   - upcoming step                      → theme/muted #e0dfe0 at 100% (bg-muted)
 * Line: 16 x 1px connector, same two states — a connector is active only when the
 * dot it leads to has been reached (step < currentStep). No opacity modifiers.
 *
 * Dots and lines are fixed-size in Figma (5 dots + 4 lines = 184px, left
 * aligned) and do not stretch, so there are no responsive variants.
 */
const DEFAULT_TOTAL_STEPS = 5

export function StepIndicator({
  currentStep,
  totalSteps = DEFAULT_TOTAL_STEPS,
}: {
  currentStep: number
  totalSteps?: number
}) {
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1)

  return (
    <div
      data-visual="progress-steps-bar"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-valuenow={currentStep}
      aria-label={`Step ${currentStep} of ${totalSteps}`}
      className="flex items-center"
    >
      {steps.map((step) => {
        const active = step <= currentStep
        // The connector after this dot leads to `step + 1`, so it is active
        // only once that next dot has been reached.
        const lineActive = step < currentStep
        return (
          <div key={step} className="flex items-center">
            <span aria-hidden="true" className="flex size-6 items-center justify-center">
              <span
                className={`size-3 rounded-full transition-colors ${
                  active ? 'bg-foreground' : 'bg-muted'
                }`}
              />
            </span>
            {step < totalSteps && (
              <span
                aria-hidden="true"
                className={`h-px w-4 transition-colors ${lineActive ? 'bg-foreground' : 'bg-muted'}`}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
